import type {
  InhouseDigitalBillOrderDetailsDto,
  InhouseDigitalBillOrderLineDetailsDto,
  InhouseDigitalBillOrderLineOptionDetailsDto,
} from '@ordr-as/ordr-service-types/epos';

const getOrdersGroupedByName = (orders: InhouseDigitalBillOrderDetailsDto[]) => {
  const ordersByName = new Map<string, InhouseDigitalBillOrderLineDetailsDto[][]>();
  const ordersDoneByWaiter = orders.filter((order) => isOrderDoneByWaiter(order));
  const ordersDoneByGuest = orders.filter((order) => !isOrderDoneByWaiter(order));

  if (ordersDoneByWaiter.length) {
    const orderLinesByWaiter = ordersDoneByWaiter.flatMap((order) => order.orderLines);
    const mergedOrderLines = mergeOrderLines(orderLinesByWaiter);
    const combinedOrderLinesByWaiter = [...mergedOrderLines.values()];
    ordersByName.set('Waiter', [...combinedOrderLinesByWaiter]);
  }

  ordersDoneByGuest.forEach((order) => {
    const { bookingDoneBy } = order;
    const name = bookingDoneBy?.name || 'Unknown';

    if (order.orderLines.length === 0) {
      return;
    }

    if (bookingDoneBy?.userType === 'SUPER_USER') {
      ordersByName.set('WAITER', [...(ordersByName.get('WAITER') || []), order.orderLines]);
      return;
    }

    if (!ordersByName.has(name)) {
      ordersByName.set(name, [order.orderLines]);
      return;
    }
    ordersByName.set(name, [...(ordersByName.get(name) || []), order.orderLines]);
  });

  const ordersByNameSorted = new Map<string, InhouseDigitalBillOrderLineDetailsDto[][]>();

  ordersByName.forEach((orderLines, name) => {
    if (name === 'Waiter') {
      ordersByNameSorted.set('Waiter', orderLines);
      return;
    }

    const orderLinesByGuest = orderLines.flat();
    const mergedOrderLines = mergeOrderLines(orderLinesByGuest);
    const combinedOrderLinesByGuest = [...mergedOrderLines.values()];
    ordersByNameSorted.set(name, [...combinedOrderLinesByGuest]);
  });

  return ordersByNameSorted;
};

const isOrderDoneByWaiter = (order: InhouseDigitalBillOrderDetailsDto) => {
  const { bookingDoneBy } = order;
  const userType = bookingDoneBy?.userType;

  return userType === 'SUPER_USER' || userType === 'ADMIN';
};

const getOptionKey = (option: InhouseDigitalBillOrderLineOptionDetailsDto) => {
  const { variantId, name, price, quantity } = option;
  return `${variantId ?? name}_${price}_${quantity}`;
};

const sortOptionKeyAscending = (key: string) => key.split(':').sort().join(':'); // Split the key by ":" and sort the array, then join it back to a
const mergeOrderLines = (orderLines: InhouseDigitalBillOrderLineDetailsDto[]) =>
  orderLines.reduce((acc, orderLine) => {
    const { variantId } = orderLine;
    const optionKey = orderLine.options?.map(getOptionKey).join(':') ?? '';
    const sortedOptionKey = sortOptionKeyAscending(optionKey);

    const commentKey = orderLine.comment ?? '';
    // Portions of a split share variantId/price/options but must render as
    // their own rows so each can be paid for independently — break the group
    // by id when splitFromOrderLineId is set.
    const splitKey = orderLine.splitFromOrderLineId == null ? '' : `_${orderLine.id}`;
    const key = (variantId ?? orderLine.name ?? '') + orderLine.price + sortedOptionKey + commentKey + splitKey;

    const existingOrderLineArray = acc.get(key);

    if (existingOrderLineArray) {
      existingOrderLineArray.push(orderLine);
      return acc;
    }

    acc.set(key, [orderLine]);
    return acc;
  }, new Map<string, InhouseDigitalBillOrderLineDetailsDto[]>());
export default getOrdersGroupedByName;
